// ============================================================
// BEATS - an attack in the log, as the display plays it (plan map-display,
// Track D; the beat grammar of the 2026-10-07 rulings).
//
// A beat is derived, never stored: an `Impact` (engine/impacts - the newest
// attack entries, watched by a high-water mark) and this screen's markers
// make a short timed plan. Melee is wind-up, strike, hit-stop, impact; a shot
// is the muzzle, the round's flight (longer the farther it goes), the
// hit-stop, the impact - and an Explosive bolt detonates late. The round is
// drawn by its damage type (Energy beam, Impact dash, Explosive bolt, Rending
// needle) and the landing leaves that type's mark. A heavy hit throws, kicks
// the camera and whites the frame; only a critical strobes.
//
// VEILED-SAFE BY CONSTRUCTION. A hidden attacker's id reaches a player's
// machine veiled (hooks/useVeil), so it matches no marker there: the beat
// has no `from` and is drawn from the edge of the screen. A target this
// screen has no marker for gets no beat at all.
//
// THE KIND OF ATTACK (plan design-gaps, K3) shapes the strike: a charge
// rushes the attacker at the target before the wind-up; an all-out attack
// winds up long and throws the target a full stride; a guarded attack sweeps
// short and holds a guard ring after; swift and lightning strike with two and
// three bars; a feint winds up and stops short, landing nothing. The rush and
// the throw are SHOVES: a figure drawn off its committed spot for the beat's
// moment (`shoveOf`), which the panel folds into the drawn tokens.
//
// Randomness (the sweep's plane) is seeded by the beat's id - never
// Math.random (gotcha 5).
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from '../battlefield/types';
import { tokenFoot, type Token } from '../battlefield/tokens';
import type { DamageCode, Impact } from '../impacts';
import { QUIET_KINDS, type AreaBurst, type AttackKind, type RateOfFire } from '../attackBeat';
import { SUPPRESSION_CONE_DEGREES } from '../attackCalc';
import { groundDistance } from '../battlefield/measure';
import { bearingBetween, finite, headingPoint, wrap180 } from '../battlefield/facing';
import { burstOf, firstLanded } from '../plate/burst';
import { BACKTRACK } from '../plate/natures';
import { playedRules, specialMoments, type SpecialRole } from '../plate/specials';
import { headPoint } from './figures';
import { between, rngFor } from './rng';
import { easeInOut, progress } from './timeline';
import type { Signature } from '../stealth/noise';

/** One round of a burst on the target (plan body-map M2): a dodged one glancing past, or a hit landing. */
export interface BeatHit {
  /** ms from the beat's start. */
  at: number;
  /** Which way a dodged round whips past; null for one that landed. */
  glance: 'left' | 'right' | null;
  blocked: boolean;
  soaked: boolean;
  crit: boolean;
}

/** A weapon special's moment on the beat, by the plate's merge (engine/plate/specials). */
export interface BeatAccent {
  key: string;
  role: SpecialRole;
  at: number;
}

/** What the target's nature does to the hit: a field's block, a daemonic soak, Warp Instability's backtrack. */
export interface BeatMoment {
  kind: 'block' | 'soak' | 'backtrack' | 'undying';
  at: number;
}

/** How long each piece of a burst plays, ms (the rattle's count stays longest, so it can be read). */
export const PIECE_MS = { hit: 600, rattle: 1400, accent: 700 };
/**
 * Each moment's life, ms: Undying's is the design page's fall (0.5 s), the lie with no life signs (to 1.5 s) and the
 * fall run backwards (to 2.3 s).
 */
export const MOMENT_MS: Record<BeatMoment['kind'], number> = {
  block: 1000,
  soak: 1000,
  backtrack: 1000,
  undying: 2400,
};
/** After the last landed hit, s, before Undying falls. */
const UNDYING_AFTER = 0.15;

/** Wounds that make a hit heavy: throw, camera kick, white frame. */
export const HEAVY_DAMAGE = 10;

/** `rush`: the charge's lunge before the wind-up; `guard`: the guarded ring held after; `feint`: the stop short. */
export type BeatPhase =
  | 'rush'
  | 'windup'
  | 'strike'
  | 'stop'
  | 'impact'
  | 'guard'
  | 'feint'
  | 'detonate'
  | 'emerge'
  /** A horde's split parting its ring, its break scattering it (V4). */
  | 'part'
  /** A blast's victim waits (T2): its scattered template sliding into place, or the primary going off before a copy. */
  | 'hold'
  /** A quiet move's gesture (Z1): Observe's wedge, a hider's fading ring, Distract's noise, Shadow's thread. */
  | 'quiet'
  | 'scatter';
type RoundLook = 'beam' | 'dash' | 'bolt' | 'needle';
type Mark = 'burn' | 'shock' | 'burst' | 'tear';

/**
 * Each landing mark's life from the landing, ms (plan design-gaps, S4): Impact's floor shock and Explosive's disc and
 * fragments as long as the landing, Energy's glow lingering, Rending's tear staying open and knitting longest.
 */
export const MARK_MS: Record<Mark, number> = { shock: 420, burst: 520, burn: 1400, tear: 1800 };

/**
 * How far into its life a piece of a beat is at `t` ms into the beat (0..1), or null outside its life; a screen that
 * holds still (`t` null) shows it at `still`. One rule for the burst's pieces and the landing's mark.
 */
export function ageOf(at: number, life: number, t: number | null, still: number): number | null {
  if (t === null) return still;
  return t < at || t >= at + life ? null : (t - at) / life;
}

/** How far into its life a still screen shows a mark: the tear open, the glow lit, the rings out a little. */
const STILL_MARK = 0.25;
/** The tear stays open this share of its life, then knits over the rest. */
const TEAR_OPEN = 0.35;

/** How far into its life the beat's mark is (0..1), or null when it is not drawn at `t` (S4). */
export const markShare = (beat: Pick<Beat, 'mark' | 'markAt'>, t: number | null): number | null =>
  beat.mark ? ageOf(beat.markAt, MARK_MS[beat.mark], t, STILL_MARK) : null;

/** How far the rending tear has knitted at `k` into its life (0 open .. 1 shut). */
export const knitOf = (k: number) => Math.max(0, (k - TEAR_OPEN) / (1 - TEAR_OPEN));

/**
 * A blast's template as the map plays it (T2): on the ground at `centre` with radius `r` m; where a scattered throw was
 * aimed (`from`, null when it went where it was aimed); a copy's step (0 the primary); and when it goes off, ms from
 * the beat's start - as its round lands. Every victim of one template carries the same; the display draws it once.
 */
export interface AreaPlay {
  centre: Vec3;
  r: number;
  from: Vec3 | null;
  copy: number;
  goesOffAt: number;
}

/** A scattered template slides from its aim for SLIDE_MS, then holds; each copy waits COPY_MS a step for the last. */
export const SLIDE_MS = 700;
const SLIDE_HOLD_MS = 400;
const COPY_MS = 1000;

/**
 * How long a template's victim holds before its beat plays: the slide (its own, or - a copy of a scattered primary -
 * the primary's), then the copies before it.
 */
const holdOf = (area: AreaBurst | undefined) =>
  area ? (area.from || area.late ? SLIDE_MS + SLIDE_HOLD_MS : 0) + area.copy * COPY_MS : 0;

export interface Beat {
  id: string;
  /** `emerge`: a hidden figure revealed, rising out of hiding (plan map-display, G2). */
  kind: 'strike' | 'shot' | 'emerge';
  /** The attacker's middle; null when this screen has no marker for them (the beat comes from the edge). */
  from: Vec3 | null;
  to: Vec3;
  /** Where a miss lands: the floor behind the target, along the line. Null when it landed. */
  missAt: Vec3 | null;
  /** The round, for a shot. */
  look: RoundLook | null;
  /** What the landing leaves, by damage type; null when nothing got through. */
  mark: Mark | null;
  /** When the mark starts, ms from the beat's start (the landing); 0 with no mark. It lives `MARK_MS[mark]`. */
  markAt: number;
  /** The floor under the target, for the marks that lie on it. */
  floor: Vec3;
  /** The target's ring radius (m): what a horde's split parts and its break scatters (V4). */
  ringM: number;
  heavy: boolean;
  crit: boolean;
  parried: boolean;
  dead: boolean;
  /** The weapon's noise, for the ring the display draws about the attacker in Stealth; null when unsaid. */
  noise: 'loud' | 'silent' | null;
  /** The weapon's Signature step (plan armoury): how far the ring spreads, at the table's reach. */
  signature: Signature | null;
  /** The strike's sweep plane, degrees off the ground's: never crosses head or ground. */
  tilt: number;
  /** The attack's kind (plan design-gaps, K3): what the strike or shot plays as. */
  attack: AttackKind;
  /** The attacker's id, for the rush; null when this screen has no marker for them. */
  attackerId: string | null;
  /** The struck figure's id (the impact's combatant): whom a throw moves. */
  targetId: string;
  /** How far the attacker lunges at the target during the rush, metres; 0 for no rush. */
  rushM: number;
  /** How far the target is thrown by the landing, metres; 0 when it stands. */
  throwM: number;
  /** A dodge took some of it: the target's ring parts as the blow passes (K5). */
  dodged: boolean;
  /** A shot's rate of fire (plan design-gaps, K4); null for a strike or an unsaid one. */
  rof: RateOfFire | null;
  /** Suppressing fire's arc on the ground, a wedge from the shooter past the target; null otherwise. */
  zone: Vec3[] | null;
  /** Where the round met the cover in front of the target, and the AP it paid; null in the open. */
  cover: { at: Vec3; ap: number } | null;
  /**
   * Where a round the armour stopped glances off to (plan design-gaps, V2): off the face of the hull it met, reflected;
   * null when it went in, missed, or was a strike.
   */
  bounce: Vec3 | null;
  /** The blast template this victim was caught in (plan design-gaps, T2); null for every other beat. */
  area: AreaPlay | null;
  /** The burst (plan body-map M2): a piece per round from the landing on, the rest one rattle with its count. */
  hits: BeatHit[];
  rattle: { at: number; count: number } | null;
  accents: BeatAccent[];
  moments: BeatMoment[];
  at: number;
  /** The whole beat, ms: past its last phase while its burst's pieces play on (they are drawn by time, not phase). */
  ms: number;
  /** How long it moves a figure or the camera, ms from its start (`movesUntil`); 0 when it moves nothing. */
  movesMs: number;
  phases: readonly { name: BeatPhase; from: number; to: number }[];
}

const LOOKS: Record<DamageCode, RoundLook> = { E: 'beam', I: 'dash', X: 'bolt', R: 'needle' };
const MARKS: Record<DamageCode, Mark> = { E: 'burn', I: 'shock', X: 'burst', R: 'tear' };

/** The phases' lengths, ms. A round flies FLIGHT_MS plus FLIGHT_MS_PER_M per metre, at most FLIGHT_MAX_MS. */
const WINDUP_MS = { strike: 220, shot: 140 };
/** An all-out attack winds up long; a guarded one sweeps short. */
const ALL_OUT_WINDUP_MS = 380;
const GUARDED_STRIKE_MS = 160;
const RUSH_MS = 260;
const GUARD_MS = 420;
const FEINT_MS = 360;
/** The charge's lunge, and the throws: an all-out landing a full stride, any heavy landing half of one. */
const RUSH_M = 1.0;
/** How near the target's middle a charge's rush may come: two men's footprints and a hand. */
const RUSH_CLEAR_M = 1.1;
const THROW_ALL_OUT_M = 1.0;
const THROW_HEAVY_M = 0.5;
/** A heavy landing kicks the camera this far along the line, over the hit-stop. */
const KICK_M = 0.35;
/** The body ram (K5): the actor lunges this share of the way at the target (at most RAM_REACH_M), over RAM_MS. */
const RAM_SHARE = 0.6;
const RAM_REACH_M = 1.4;
const RAM_MS = 220;
/** A ram that won shoves the target; Knock-Down throws it twice as far. */
const RAM_THROW_M = 0.5;
const KNOCKDOWN_THROW_M = 1.0;
/** A won Manoeuvre shoves the loser one metre - the rule's own distance (Z1). */
const MANOEUVRE_M = 1;
/** A parry throws the striker back this far and lets them go, over the landing (K5). */
const PARRY_THROW_M = 0.6;
/** Suppressing fire's wedge reaches this far past the target, and is drawn with this many points on its arc. */
const ZONE_PAST_M = 2;
const ZONE_ARC_POINTS = 7;
/** The cover's sparks stand this far in front of the target, along the line, at this height above its feet. */
const COVER_IN_FRONT_M = 0.6;
const COVER_HEIGHT_M = 0.5;
const STRIKE_MS = 240;
const STOP_MS = 90;
const IMPACT_MS = 420;
const DETONATE_DELAY_MS = 260;
const DETONATE_MS = 380;
const FLIGHT_MS = 120;
const FLIGHT_MS_PER_M = 9;
const FLIGHT_MAX_MS = 520;
const MAX_TILT = 35;
/** How long a revealed figure takes to rise out of hiding, ms. */
const EMERGE_MS = 1200;
/** How far behind the target a miss lands, metres. */
const MISS_PAST_M = 2;

type Placed = Pick<Token, 'x' | 'z' | 'elevation' | 'eyeHeight'> & {
  height?: number;
  heading?: number | null;
  footprint?: number;
};

/** The beat this screen plays for an impact, or null: a jam (nothing went off), or no marker for the target here. */
export function beatOf(
  impact: Impact,
  tokens: Readonly<Record<string, Placed>>,
  /** The target is Warp Instability's: it backtracks once a hit lands. */
  warps = false,
): Beat | null {
  const target = tokens[impact.combatantId];
  if (!target || impact.outcome === 'jam') return null;
  const attacker = impact.attackerId ? tokens[impact.attackerId] : undefined;
  const to = headPoint(target);
  const from = fromOf(impact, attacker, target, to);
  const kind = kindOf(impact);
  const landed = impact.damage > 0;
  const shape = shapeOf(impact, kind, from, to);
  const phases = phasesOf(kind, landed || !!impact.won, from, to, shape, holdOf(impact.area));
  const pieces = shape.plays ? piecesOf(impact, landsAt(phases), warps) : NO_PIECES;
  const landing = landingOf(impact, kind, landed && shape.plays, from, target);
  const beat: Beat = {
    id: impact.id,
    kind,
    from,
    to,
    ...landing,
    ...strikeOf(impact, shape, attacker ? impact.attackerId : null, landing.heavy && landed),
    ...shotOf(impact, kind, from, target, landed),
    area: areaPlayOf(impact.area, target, phases),
    tilt: between(rngFor(impact.id, 'tilt'), -MAX_TILT, MAX_TILT),
    ...pieces,
    at: impact.at,
    ...markTimeOf(landing.mark, phases, lastOf(phases, pieces)),
    movesMs: 0,
    phases,
  };
  return { ...beat, movesMs: movesUntil(beat) };
}

/** Where the beat comes from: a copy's spawner (T2), else the attacker's place, else nowhere this screen holds. */
function fromOf(impact: Impact, attacker: Placed | undefined, target: Placed, to: Vec3): Vec3 | null {
  if (impact.area?.copy) return groundAt(impact.area, to.y);
  return attacker ? attackFrom(attacker, target) : null;
}

/** A point of the template at a height: its centre, or a scattered throw's aim. */
const groundAt = (p: { x: number; z: number }, y: number): Vec3 => ({ x: p.x, y, z: p.z });

/** The template as this beat plays it (T2), going off as the round lands; null with none. */
function areaPlayOf(area: AreaBurst | undefined, target: Placed, phases: Phases): AreaPlay | null {
  if (!area) return null;
  const y = target.elevation;
  const { r, copy } = area;
  return {
    centre: groundAt(area, y),
    r,
    from: area.from ? groundAt(area.from, y) : null,
    copy,
    goesOffAt: landsAt(phases),
  };
}

/** From this footprint up (m: a horde, a hull) an attack leaves from the ring's edge, this share of it out. */
const EDGE_FROM_M = 1;
const EDGE_SHARE = 0.8;

/**
 * Where an attack leaves from: a person's middle; a big body's ring edge toward the target (V3: a horde's attacks
 * come from its edge bodies, a hull's from its side).
 */
function attackFrom(attacker: Placed, target: Placed): Vec3 {
  const head = headPoint(attacker);
  const r = attacker.footprint ?? 0;
  if (r < EDGE_FROM_M) return head;
  const d = groundDir(attacker, target);
  return { x: head.x + d.x * r * EDGE_SHARE, y: head.y, z: head.z + d.z * r * EDGE_SHARE };
}

/** What the beat is: a reveal's emergence, else a melee strike or a shot. */
const kindOf = (impact: Impact): Beat['kind'] => {
  if (impact.outcome === 'emerge') return 'emerge';
  return impact.melee ? 'strike' : 'shot';
};

/** What the attack was made as: a feint is the body action that lands nothing; else the kind off the beat. */
const attackOf = (impact: Impact): AttackKind => impact.kind ?? 'standard';

/** What the attack's kind gives the beat (K3): the kind, who rushes, the bars, the rush and the throw. */
function strikeOf(impact: Impact, shape: Shape, attackerId: string | null, heavyLanding: boolean) {
  return {
    attack: shape.attack,
    attackerId,
    targetId: impact.combatantId,
    rushM: shape.rushM,
    throwM: shape.attack === 'ram' ? ramThrowOf(impact) : throwOf(shape.attack, heavyLanding, impact.damage > 0),
    dodged: impact.dodged > 0,
  };
}

/** When the mark starts and how long the whole beat lasts with it (a lingering mark outlasts the burst). */
function markTimeOf(mark: Mark | null, phases: Phases, last: number): { markAt: number; ms: number } {
  if (!mark) return { markAt: 0, ms: last };
  const markAt = phases.find((p) => p.name === 'impact')?.from ?? 0;
  return { markAt, ms: Math.max(last, markAt + MARK_MS[mark]) };
}

/**
 * Which way a killing blow's target falls (plan design-gaps, S4): away from the attacker, along the line of the blow
 * (degrees, the facing's bearing); null when the blow did not kill or this screen has no attacker to fall from.
 */
export const fallHeadingOf = (beat: Beat): number | null =>
  beat.dead && beat.from ? bearingBetween(beat.from, beat.to) : null;

/** A body ram that won shoves the target, Knock-Down twice as far; one that lost shoves nobody. */
function ramThrowOf(impact: Impact): number {
  // A body action throws when it won; a vehicle's ram (an attack line) when it landed (the Track K review).
  const struck = impact.won ?? impact.damage > 0;
  if (!struck) return 0;
  if (impact.action === 'manoeuvre') return MANOEUVRE_M;
  return impact.action === 'knockDown' ? KNOCKDOWN_THROW_M : RAM_THROW_M;
}

/** A shot's rate of fire as the beat draws it (K4): the rounds, suppressing fire's wedge, the cover's sparks. */
function shotOf(impact: Impact, kind: Beat['kind'], from: Vec3 | null, target: Placed, landed: boolean) {
  const rof = kind === 'shot' ? (impact.rof ?? null) : null;
  const ap = landed && from ? (impact.coverAp ?? 0) : 0;
  return {
    rof,
    zone: rof === 'suppress' && from ? zoneOf(from, target) : null,
    cover: ap > 0 && from ? { at: coverPoint(from, target), ap } : null,
    bounce: kind === 'shot' && armourStopped(impact) ? bouncePoint(impact.id, from, target) : null,
  };
}

/**
 * The armour stopped it: a round that met the target and did nothing, read by what happened rather than its label -
 * the log labels a no-damage hit no defence touched `hit` (structuredNarrative's `landedOutcome`), keeping
 * `deflected` for one a defence also met - and not a force field's block, whose cells play instead.
 */
const armourStopped = (impact: Impact) =>
  impact.damage === 0 &&
  !['miss', 'jam', 'emerge', 'action'].includes(impact.outcome) &&
  !impact.hits.some((h) => h.blocked) &&
  // A shot dodged whole never met the armour: its rounds whip past.
  !(impact.dodged > 0 && impact.hits.length === 0);

/** How far the glancing round flies off the hull, metres, and how far off square a face with no heading turns it. */
const BOUNCE_M = 1.6;
const UNSET_FACE_DEG = 35;

/**
 * Where a round the armour stopped glances off to: its line reflected about the face it met - of a hull's four faces
 * (by its heading), the one turned most toward the shooter; with no heading, a face square to the shot turned a
 * seeded few degrees, so the bounce still reads as one.
 */
function bouncePoint(id: string, from: Vec3 | null, target: Placed): Vec3 {
  const d = groundDir(from, target);
  const n = faceNormal(id, d, target.heading);
  const dot = d.x * n.x + d.z * n.z;
  const r = { x: d.x - 2 * dot * n.x, z: d.z - 2 * dot * n.z };
  const head = headPoint(target);
  return { x: head.x + r.x * BOUNCE_M, y: head.y, z: head.z + r.z * BOUNCE_M };
}

/**
 * The outward normal of the face a round on line `d` meets (a unit vector on the ground): of a hull's four faces, the
 * one nearest square to the line back toward the shooter; with no heading, that square turned a seeded few degrees.
 */
function faceNormal(id: string, d: { x: number; z: number }, heading: number | null | undefined) {
  const square = bearingBetween(d, { x: 0, z: 0 });
  const face = finite(heading)
    ? heading + 90 * Math.round(wrap180(square - heading) / 90)
    : square + between(rngFor(id, 'bounce'), -UNSET_FACE_DEG, UNSET_FACE_DEG);
  return headingPoint({ x: 0, z: 0 }, face, 1);
}

/** How many rounds a shot draws: one, two for semi-auto, a stream of four for full auto and suppressing fire. */
export const roundsOf = (rof: RateOfFire | null): 1 | 2 | 4 =>
  rof === 'semi' ? 2 : rof === 'full' || rof === 'suppress' ? 4 : 1;

/**
 * The ground's unit vector from a point toward the target, and the distance: +x when there is no point (an unseen
 * attacker) or they stand on one spot. The one direction every beat's line takes.
 */
function groundDir(
  from: Pick<Vec3, 'x' | 'z'> | null,
  to: Pick<Vec3, 'x' | 'z'>,
): { x: number; z: number; len: number } {
  const len = from ? Math.hypot(to.x - from.x, to.z - from.z) : 0;
  return from && len ? { x: (to.x - from.x) / len, z: (to.z - from.z) / len, len } : { x: 1, z: 0, len };
}

/** Suppressing fire's arc (SUPPRESSION_CONE_DEGREES wide) from the shooter to past the target, on its ground. */
function zoneOf(from: Vec3, target: Placed): Vec3[] {
  const r = groundDir(from, target).len + ZONE_PAST_M;
  const bearing = bearingBetween(from, target);
  const y = target.elevation + 0.03;
  const arc = Array.from({ length: ZONE_ARC_POINTS }, (_, i) => {
    const heading = bearing - SUPPRESSION_CONE_DEGREES / 2 + (SUPPRESSION_CONE_DEGREES * i) / (ZONE_ARC_POINTS - 1);
    return { ...headingPoint(from, heading, r), y };
  });
  return [{ x: from.x, y, z: from.z }, ...arc];
}

/** Where the round meets the cover: in front of the target along the line, at a crouching height. */
function coverPoint(from: Vec3, target: Placed): Vec3 {
  const dir = groundDir(from, target);
  return {
    x: target.x - dir.x * COVER_IN_FRONT_M,
    y: target.elevation + COVER_HEIGHT_M,
    z: target.z - dir.z * COVER_IN_FRONT_M,
  };
}

/** The strike's bars by kind: one, two for a swift attack, three for a lightning one. */
export const barsOf = (attack: AttackKind): 1 | 2 | 3 => (attack === 'swift' ? 2 : attack === 'lightning' ? 3 : 1);

/**
 * How far the landing throws the target: an all-out attack a stride, any heavy hit half of one, else it stands.
 * (Impact's shove is drawn in its mark, a skid on the floor, S4: moving the figure would run the panel's frame for
 * every Impact landing.)
 */
function throwOf(attack: AttackKind, heavy: boolean, landed: boolean): number {
  if (!landed) return 0;
  if (attack === 'allOut') return THROW_ALL_OUT_M;
  return heavy ? THROW_HEAVY_M : 0;
}

/** How the beat lands: the round and the mark by damage type, what the hit was, and where a miss lands. */
function landingOf(impact: Impact, kind: Beat['kind'], landed: boolean, from: Vec3 | null, target: Placed) {
  const type = impact.damageType;
  return {
    missAt: impact.outcome === 'miss' ? missPoint(from, target) : null,
    look: kind === 'shot' && type ? LOOKS[type] : null,
    mark: landed && type ? MARKS[type] : null,
    floor: tokenFoot(target),
    ringM: target.footprint ?? 0.5,
    heavy: impact.damage >= HEAVY_DAMAGE || impact.crit || impact.dead,
    crit: impact.crit,
    parried: impact.parried,
    dead: impact.dead,
    noise: impact.noise ?? null,
    signature: impact.signature ?? null,
  };
}

/** The floor behind the target along the line from the attacker (or the world's +x with no attacker). */
function missPoint(from: Vec3 | null, target: Placed): Vec3 {
  const dir = groundDir(from, target);
  return { x: target.x + dir.x * MISS_PAST_M, y: target.elevation, z: target.z + dir.z * MISS_PAST_M };
}

/** The kinds that land nothing - no pieces, no mark: a feint, a horde's split or break. */
const LANDS_NOTHING: ReadonlySet<AttackKind> = new Set(['feint', 'split', 'break', ...QUIET_KINDS]);
/** A quiet move's gesture lasts this long (Z1): Observe's sweep, then its hold. */
const QUIET_MS = 1600;
const QUIET: ReadonlySet<AttackKind> = new Set(QUIET_KINDS);
/** A horde's split and break, each one phase, ms. */
const HORDE_MS = 900;

/** How the attack's kind shapes the beat: the charge's rush, the bolt's detonation, whether anything lands at all. */
interface Shape {
  attack: AttackKind;
  /** How far the attacker lunges: the charge's metre, or the ram's share of the gap. */
  rushM: number;
  detonates: boolean;
  /** A reveal and a feint land nothing: no pieces, no mark. */
  plays: boolean;
}

function shapeOf(impact: Impact, kind: Beat['kind'], from: Vec3 | null, to: Vec3): Shape {
  const attack = attackOf(impact);
  const rushM = from ? rushMetres(attack, from, to) : 0;
  return {
    attack,
    rushM,
    detonates: impact.damageType === 'X' && !impact.melee,
    plays: kind !== 'emerge' && !LANDS_NOTHING.has(attack),
  };
}

type Lengths = [BeatPhase, number][];
/** A phase that is there only when `when` holds. */
const opt = (when: boolean, name: BeatPhase, ms: number): Lengths => (when ? [[name, ms]] : []);

/** The body ram's phases: the wind-up, the lunge, the hit-stop when it won, the landing. */
function ramLengths(windup: number, won: boolean): Lengths {
  return [['windup', windup], ['rush', RAM_MS], ...opt(won, 'stop', STOP_MS), ['impact', IMPACT_MS]];
}

/** How far the attacker lunges: a charge a metre before its strike; a ram most of the gap, the lunge its blow. */
function rushMetres(attack: AttackKind, from: Vec3, to: Vec3): number {
  // The last stride of the run, never into the target: a charge now ends about a metre short (engine/battlefield
  // /charge), and the old fixed metre put the striker on top of it (the user, 2026-10-21).
  if (attack === 'charge') return Math.min(RUSH_M, Math.max(0, groundDistance(from, to) - RUSH_CLEAR_M));
  if (attack !== 'ram') return 0;
  return Math.min(RAM_REACH_M, groundDistance(from, to) * RAM_SHARE);
}

/** The phases' lengths in order: a feint winds up and stops short; the rest wind up, strike, stop, land, guard. */
function lengthsOf(kind: Beat['kind'], landed: boolean, flight: number, shape: Shape): Lengths {
  // A horde's own beat (V4): its ring parts, or scatters.
  if (shape.attack === 'split') return [['part', HORDE_MS]];
  // A quiet move (Z1): one gesture, no blow.
  if (QUIET.has(shape.attack)) return [['quiet', QUIET_MS]];
  if (shape.attack === 'break') return [['scatter', HORDE_MS]];
  const windup = windupOf(kind, shape.attack);
  if (shape.attack === 'feint')
    return [
      ['windup', windup],
      ['feint', FEINT_MS],
    ];
  // The body ram: wound up, the lunge IS the blow (no bar), the hit-stop when it won.
  if (shape.attack === 'ram') return ramLengths(windup, landed);
  return strikeLengths(windup, landed, flight, shape);
}

/** The wind-up: an all-out attack's long one, else the shot's or the strike's. */
const windupOf = (kind: Beat['kind'], attack: AttackKind) =>
  attack === 'allOut' ? ALL_OUT_WINDUP_MS : kind === 'shot' ? WINDUP_MS.shot : WINDUP_MS.strike;

/** A strike's or shot's phases: the charge's rush, the wind-up, the blow, the hit-stop, the landing, the guard. */
function strikeLengths(windup: number, landed: boolean, flight: number, shape: Shape): Lengths {
  const guarded = shape.attack === 'guarded';
  return [
    ...opt(shape.rushM > 0, 'rush', RUSH_MS),
    ['windup', windup],
    ['strike', guarded ? GUARDED_STRIKE_MS : flight],
    ...opt(landed, 'stop', STOP_MS),
    ['impact', IMPACT_MS],
    ...opt(guarded, 'guard', GUARD_MS),
  ];
}

/** The phases in order, ms from the beat's start; the hit-stop only when it landed, the detonation for a bolt. */
function phasesOf(kind: Beat['kind'], landed: boolean, from: Vec3 | null, to: Vec3, shape: Shape, hold = 0): Phases {
  // A figure out of hiding rises in one long phase: nothing struck, nothing flew.
  if (kind === 'emerge') return [{ name: 'emerge' as BeatPhase, from: 0, to: EMERGE_MS }];
  const played = playedPhases(kind, landed, from, to, shape);
  // A template's victim waits its turn (T2): every phase after the hold, and so every piece and mark.
  if (!hold) return played;
  return [{ name: 'hold', from: 0, to: hold }, ...played.map((p) => ({ ...p, from: p.from + hold, to: p.to + hold }))];
}

/** The phases from the wind-up on, ms from their start. */
function playedPhases(kind: Beat['kind'], landed: boolean, from: Vec3 | null, to: Vec3, shape: Shape): Phases {
  const metres = from ? Math.hypot(to.x - from.x, to.y - from.y, to.z - from.z) : 0;
  const flight = kind === 'shot' ? Math.min(FLIGHT_MAX_MS, FLIGHT_MS + metres * FLIGHT_MS_PER_M) : STRIKE_MS;
  const lengths = lengthsOf(kind, landed, flight, shape);
  const phases: { name: BeatPhase; from: number; to: number }[] = [];
  let t = 0;
  for (const [name, ms] of lengths) {
    phases.push({ name, from: t, to: t + ms });
    t += ms;
  }
  if (shape.detonates)
    phases.push({ name: 'detonate', from: t + DETONATE_DELAY_MS, to: t + DETONATE_DELAY_MS + DETONATE_MS });
  return phases;
}

type Pieces = Pick<Beat, 'hits' | 'rattle' | 'accents' | 'moments'>;
const NO_PIECES: Pieces = { hits: [], rattle: null, accents: [], moments: [] };
type Phases = { name: BeatPhase; from: number; to: number }[];

/** When the round lands (or the blade arrives): the end of the strike, ms from the beat's start. */
const landsAt = (phases: Phases) => phases.find((p) => p.name === 'strike')?.to ?? 0;

/**
 * The burst as the map plays it, from the landing on: the plate's own timing (engine/plate/burst - dodged rounds
 * glancing first, then each hit in turn, past six one rattle; every hit counts, as on a crowd), the specials by the
 * plate's merge, and what the target's nature does - a field's block and a daemonic soak at their hit, Warp
 * Instability's backtrack once a hit lands.
 */
function piecesOf(impact: Impact, land: number, warps: boolean): Pieces {
  const burst = burstOf(impact, null);
  const ms = (s: number) => Math.round(land + s * 1000);
  const glances = burst.glances.map((g): BeatHit => ({
    at: ms(g.at),
    glance: g.side,
    blocked: false,
    soaked: false,
    crit: false,
  }));
  const hits = burst.hits.map((h): BeatHit => ({
    at: ms(h.at),
    glance: null,
    blocked: h.blocked,
    soaked: h.soaked,
    crit: h.crit,
  }));
  const first = firstLanded(burst);
  const moments: BeatMoment[] = [
    ...burst.hits.filter((h) => h.blocked).map((h): BeatMoment => ({ kind: 'block', at: ms(h.at) })),
    ...burst.hits.filter((h) => h.soaked).map((h): BeatMoment => ({ kind: 'soak', at: ms(h.at) })),
    ...(warps && first ? [{ kind: 'backtrack' as const, at: ms(first.at + BACKTRACK.delay) }] : []),
    ...(impact.cheatedDeath && first
      ? [{ kind: 'undying' as const, at: ms(Math.max(...burst.hits.map((h) => h.at)) + UNDYING_AFTER) }]
      : []),
  ];
  return {
    hits: [...glances, ...hits],
    rattle: burst.rattle && { at: ms(burst.rattle.at), count: burst.rattle.places.length },
    accents: specialMoments(playedRules(burst), burst.hits).map((m) => ({ key: m.key, role: m.role, at: ms(m.at) })),
    moments,
  };
}

/** When the beat is over, ms from its start: its last phase, or its burst's last piece if that plays on past it. */
function lastOf(phases: Phases, p: Pieces): number {
  const ends = [
    phases.at(-1)!.to,
    ...p.hits.map((h) => h.at + PIECE_MS.hit),
    ...(p.rattle ? [p.rattle.at + PIECE_MS.rattle] : []),
    ...p.accents.map((a) => a.at + PIECE_MS.accent),
    ...p.moments.map((m) => m.at + MOMENT_MS[m.kind]),
  ];
  return Math.max(...ends);
}

/** A figure drawn off its committed spot for the beat's moment: who, and by how much along the beat's line. */
export interface Shove extends GroundShift {
  id: string;
}

/** The beat's line on the ground: from the attacker to the target, or +x with no attacker. */
const lineOf = (beat: Beat) => groundDir(beat.from, beat.to);

/** The rush's share at `t` ms in: none before the lunge, lunging in, held, eased back over the impact; none after. */
function rushShare(beat: Beat, t: number): number {
  const rush = beat.phases.find((p) => p.name === 'rush');
  const impact = beat.phases.find((p) => p.name === 'impact');
  if (!rush || !impact || t < rush.from || t >= impact.to) return 0;
  if (t < rush.to) return easeInOut((t - rush.from) / (rush.to - rush.from));
  return t < impact.from ? 1 : 1 - easeInOut((t - impact.from) / (impact.to - impact.from));
}

/** A parried striker's throw-back over the landing: out and let go. Negative: away from the target. */
const parryShare = (beat: Beat, phase: BeatPhase | null, k: number) =>
  beat.parried && phase === 'impact' ? -Math.sin(Math.PI * k) : 0;

/** The throw's share at a phase: out through the hit-stop, eased back over the impact; none elsewhere. */
function throwShare(phase: BeatPhase | null, k: number): number {
  if (phase === 'stop') return k;
  return phase === 'impact' ? 1 - easeInOut(k) : 0;
}

/**
 * The attacker's own shove at `t`: the rush or the ram, a parry's throw-back. A shot's recoil is drawn at the
 * muzzle by the beats layer, never a shove: a plain shot must not tick the whole panel (the Track K simplify).
 */
const ownShove = (beat: Beat, t: number, phase: BeatPhase | null, k: number) =>
  beat.rushM * rushShare(beat, t) + PARRY_THROW_M * parryShare(beat, phase, k);

/** A shove of `metres` along the beat's line, or none. */
function along(beat: Beat, id: string | null, metres: number): Shove[] {
  if (!id || metres === 0) return [];
  const line = lineOf(beat);
  return [{ id, dx: line.x * metres, dz: line.z * metres }];
}

/**
 * Every shove this beat gives at `now` (plan design-gaps, K3/K5): the attacker's own - the rush or the ram, the
 * recoil, a parry's throw-back - and the target's throw, BOTH at once (a won Knock-Down lunges and throws).
 */
export function shovesAt(beat: Beat, now: number): Shove[] {
  const { phase, k } = phaseAt(beat, now);
  const own = beat.attackerId ? ownShove(beat, now - beat.at, phase, k) : 0;
  return [...along(beat, beat.attackerId, own), ...along(beat, beat.targetId, beat.throwM * throwShare(phase, k))];
}

/** A beat as a screen draws it this frame: where it is (`t` ms in; null on a screen that holds still). */
export interface DrawnBeat {
  beat: Beat;
  phase: BeatPhase | null;
  k: number;
  t: number | null;
}

/** An offset on the ground, metres. */
export interface GroundShift {
  dx: number;
  dz: number;
}

const NO_SHOVES: Readonly<Record<string, GroundShift>> = Object.freeze({});

/** Every figure's shove this frame, summed by figure; the one frozen empty record when nobody is shoved. */
export function shovesOf(drawn: readonly DrawnBeat[]): Readonly<Record<string, GroundShift>> {
  const out: Record<string, GroundShift> = {};
  for (const d of drawn) {
    if (d.t === null) continue;
    for (const s of shovesAt(d.beat, d.beat.at + d.t)) {
      const was = out[s.id] ?? { dx: 0, dz: 0 };
      out[s.id] = { dx: was.dx + s.dx, dz: was.dz + s.dz };
    }
  }
  return Object.keys(out).length ? out : NO_SHOVES;
}

/** When a phase ends, ms from the beat's start; 0 when the beat has none. */
const endOf = (beat: Beat, name: BeatPhase) => beat.phases.find((p) => p.name === name)?.to ?? 0;

/**
 * How long this beat moves a figure or the camera, ms from its start (the Track K review): the rush, the parry's
 * throw-back and the throw to the end of the landing, the kick to the end of the hit-stop; 0 when it moves
 * nothing. Read once into `movesMs`; the panel's clock runs only this long, the beats layer keeps its own.
 */
function movesUntil(beat: Beat): number {
  const ends = [
    beat.attackerId && (beat.rushM || beat.parried) ? endOf(beat, 'impact') : 0,
    beat.throwM ? endOf(beat, 'impact') : 0,
    beat.heavy ? endOf(beat, 'stop') : 0,
  ];
  return Math.max(...ends);
}

/**
 * The camera's kick this frame (plan design-gaps, K3): a heavy landing jolts the view along the line of the blow
 * over the hit-stop, then it is gone; null otherwise (and always on a screen that holds still).
 */
export function kickOf(drawn: readonly DrawnBeat[]): GroundShift | null {
  const hit = drawn.find((d) => d.t !== null && d.phase === 'stop' && d.beat.heavy);
  if (!hit) return null;
  const line = lineOf(hit.beat);
  const m = KICK_M * (1 - hit.k);
  return { dx: line.x * m, dz: line.z * m };
}

/** Which phase a beat is in at `now`, and how far through it (0..1); phase null before it starts or once over. */
export function phaseAt(beat: Beat, now: number): { phase: BeatPhase | null; k: number } {
  const t = now - beat.at;
  const p = beat.phases.find((ph) => t >= ph.from && t < ph.to);
  return p ? { phase: p.name, k: progress(now, beat.at + p.from, p.to - p.from) } : { phase: null, k: 0 };
}
